import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  AlertCircle,
  Loader2,
  Mic,
  MicOff,
  ShieldCheck,
  Sparkles,
  Volume2,
  VolumeX,
} from 'lucide-react'
import { SocShell } from '../components/soc/SocShell'
import {
  answerInterview,
  completeInterview,
  getInterviewScript,
  getInterviewStatus,
  startInterview,
  type InterviewSession,
} from '../lib/api'
import {
  appendSpokenDraft,
  canUseSpeechRecognition,
  canUseSpeechSynthesis,
  finalTranscriptFromEvent,
  getSpeechRecognitionCtor,
  type BrowserSpeechRecognitionLike,
} from '../lib/browserSpeech'

/**
 * The onboarding interview — "the first meeting with your new security lead, who
 * sits down and gets to know your world" (PRODUCT_VISION_agent_team.md,
 * "Onboarding as an INTERVIEW, not a form"). NOT a settings wizard: one question
 * at a time, in the Concierge's voice, answered in a calm, generous space.
 *
 * Every question renders EXACTLY as the backend sends it — the copy is the
 * Concierge's voice and lives in the server script (app/onboarding/interview.py);
 * the FE never paraphrases a question. start is idempotent, so closing the tab
 * mid-way and coming back resumes at the same step with no data loss.
 *
 * Backend: app/routers/interview.py — POST /interview/start (resume-or-create),
 * POST /interview/{id}/answer (route the answer, return the NEXT question and an
 * opt-in one-line bridge), POST /interview/{id}/complete (warm close).
 */

// The client answers in prose. The backend's per-step schemas are typed, so we
// map the prose into each step's natural free-text sink — never inventing
// structure the client didn't give us, and clamping to each field's max length
// so a long answer is preserved (truncated) rather than rejected.
//
// `three_am` is the exception: the page collects the phone number and explicit
// 3am-call consent in dedicated fields (see handleSubmit), because those fields
// drive the real out-of-band SMS/call path. We never stuff prose into them, and
// composeAnswer is not used for that step.
function composeAnswer(
  stepId: string,
  text: string,
): Record<string, unknown> {
  const t = text.trim()
  switch (stepId) {
    case 'people':
      // The client's own words ARE the row — no invented display name.
      return t
        ? { people: [{ name: t.slice(0, 200), authority: null }] }
        : { people: [] }
    case 'escalation':
      return t ? { notes: t.slice(0, 2000) } : {}
    case 'three_am':
      // Never reached — handleSubmit builds this step's answer from the
      // dedicated phone/consent fields. Kept as a safe empty fallback.
      return {}
    case 'travel':
      return t
        ? { travellers: [{ name: t.slice(0, 200), travel_pattern: null }] }
        : { travellers: [] }
    case 'watch':
      // The client's own words ARE the single watch-list entry — never split
      // on commas, never routed to notes (the backend only reads watch_list).
      return t ? { watch_list: [t.slice(0, 2000)] } : { watch_list: [] }
    case 'disaster':
      // The client's own words ARE the single crown-jewels entry — never split
      // on commas, never routed to scenario (the backend only reads
      // crown_jewels into the PoL sensitivities).
      return t ? { crown_jewels: [t.slice(0, 2000)] } : { crown_jewels: [] }
    default:
      return {}
  }
}

/**
 * DW-CLIENT-HOME-FIRST-MEETING-IN-PLACE-KEEPS-WORD-DECOY-ON-HOME.
 *
 * `embedded` mounts this same meeting INSIDE /client home, under the tripwire
 * band, instead of as its own page: same questions, same server copy, same
 * read-only start guard — only the page chrome (min-h-screen, ClientNavigation
 * and the <main> landmark) is dropped, because home already owns those. A
 * household who has not sat the first meeting can therefore answer it without
 * ever leaving the one holding picture, so HomeTripwiresCard — and "Download
 * Decoy (.docx)" on that section — stays on screen the whole time
 * (DigitalWatch.md: "know the person" AND "canaries are in the same picture").
 *
 * `onDone` is the embedded warm close's one door: on a page there is somewhere
 * to go (/client), embedded there is not — the household is already home, so
 * the host unmounts the meeting and home is simply home again. Never used to
 * start anything; `load()` still asks status first and never starts after a
 * complete meeting, embedded or not.
 */
export function OnboardingInterviewPage({
  embedded = false,
  onDone,
}: {
  embedded?: boolean
  onDone?: () => void
} = {}) {
  const [session, setSession] = useState<InterviewSession | null>(null)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [draft, setDraft] = useState('')
  // three_am only: the number the analyst would actually call, and the explicit
  // "call me at 3am" consent. Default off — consent is never assumed.
  const [phone, setPhone] = useState('')
  const [consent, setConsent] = useState(false)
  const [bridge, setBridge] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Free browser speech (Web Speech API only — never a paid STT path).
  // Recognition fills the same `draft` the textarea uses; TTS reads the
  // backend prompt (+ optional bridge) verbatim. Mic starts only on Talk.
  const recognitionOk = useMemo(() => canUseSpeechRecognition(), [])
  const synthesisOk = useMemo(() => canUseSpeechSynthesis(), [])
  const [listening, setListening] = useState(false)
  // Default unmuted so we attempt one speak when a question appears; browsers
  // may still block autoplay — Speak click is enough.
  const [speakMuted, setSpeakMuted] = useState(false)
  const [voiceNote, setVoiceNote] = useState<string | null>(null)

  // Guards the auto-finalise (a resumed session that is already fully answered
  // but not yet marked complete) so it fires at most once.
  const finalizingRef = useRef(false)
  const recognitionRef = useRef<BrowserSpeechRecognitionLike | null>(null)
  const spokenQuestionKeyRef = useRef<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      // Status first: after complete, POST /interview/start would create a new
      // meeting. Gate holds; backend start stays resume-or-create for none /
      // in_progress only. Status throw must not start either.
      const st = await getInterviewStatus()
      if (st.status === 'complete') {
        // Local stub so existing CompleteView branch renders without POSTing.
        setSession({
          id: st.id ?? 'complete',
          principal_id: '',
          status: 'complete',
          current_step: 0,
          answered_steps: [],
          answers: {},
          next_question: null,
          started_at: null,
          completed_at: null,
        })
        return
      }
      // start is idempotent (resume-or-create); the script gives the total for
      // the "3 of 6" whisper without hardcoding the count in the FE.
      const [script, started] = await Promise.all([
        getInterviewScript(),
        startInterview(),
      ])
      setTotal(script.steps.length)
      setSession(started)
    } catch {
      setLoadError('We couldn’t start your interview just now.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // A resumed session with every step answered but not yet completed: finish it
  // (idempotent on the backend) so the client lands on the warm close, not a
  // dead question-less screen.
  useEffect(() => {
    if (
      session &&
      session.status === 'in_progress' &&
      session.next_question === null &&
      !finalizingRef.current
    ) {
      finalizingRef.current = true
      completeInterview(session.id)
        .then(setSession)
        .catch(() => {
          finalizingRef.current = false
          setSubmitError('We couldn’t wrap things up just now. Please try again.')
        })
    }
  }, [session])

  const question = session?.next_question ?? null

  const cancelSpeech = useCallback(() => {
    if (typeof window === 'undefined' || !canUseSpeechSynthesis()) return
    try {
      window.speechSynthesis.cancel()
    } catch {
      // Best-effort — some browsers throw if nothing is speaking.
    }
  }, [])

  const stopListening = useCallback(() => {
    setListening(false)
    const rec = recognitionRef.current
    recognitionRef.current = null
    if (!rec) return
    try {
      rec.onresult = null
      rec.onerror = null
      rec.onend = null
      rec.stop()
    } catch {
      try {
        rec.abort()
      } catch {
        // Ignore stop/abort races when the engine already ended.
      }
    }
  }, [])

  const speakQuestionText = useCallback(
    (prompt: string, bridgeLine: string | null) => {
      if (!canUseSpeechSynthesis()) return
      cancelSpeech()
      const parts = [bridgeLine?.trim(), prompt.trim()].filter(
        (p): p is string => Boolean(p),
      )
      if (parts.length === 0) return
      const utter = new SpeechSynthesisUtterance(parts.join(' '))
      try {
        window.speechSynthesis.speak(utter)
      } catch {
        // Autoplay / engine refusal — Speak click can retry.
      }
    },
    [cancelSpeech],
  )

  const startListening = useCallback(() => {
    // Prose steps only — never dictate three_am phone/consent fields.
    if (!question || question.id === 'three_am') return
    const Ctor = getSpeechRecognitionCtor()
    if (!Ctor) {
      setVoiceNote(
        'Voice typing isn’t available in this browser. You can still type your answer.',
      )
      return
    }
    stopListening()
    setVoiceNote(null)
    const rec = new Ctor()
    rec.continuous = false
    rec.interimResults = false
    rec.lang = 'en-US'
    rec.onresult = (ev) => {
      const spoken = finalTranscriptFromEvent(ev)
      if (!spoken) return
      setDraft((prev) => appendSpokenDraft(prev, spoken))
    }
    rec.onerror = () => {
      // Honest: no fake transcript. Keep whatever is already in the draft.
      setListening(false)
      recognitionRef.current = null
      setVoiceNote(
        'We couldn’t hear that just now. Your typed answer is still here.',
      )
    }
    rec.onend = () => {
      setListening(false)
      recognitionRef.current = null
    }
    recognitionRef.current = rec
    try {
      rec.start()
      setListening(true)
    } catch {
      recognitionRef.current = null
      setListening(false)
      setVoiceNote(
        'We couldn’t start the microphone. You can still type your answer.',
      )
    }
  }, [question, stopListening])

  // Stop mic + cancel TTS when the question changes; optionally speak once.
  useEffect(() => {
    stopListening()
    cancelSpeech()
    if (!question) {
      spokenQuestionKeyRef.current = null
      return
    }
    const key = `${question.id}:${question.prompt}:${bridge ?? ''}`
    if (speakMuted) {
      spokenQuestionKeyRef.current = key
      return
    }
    if (!synthesisOk) return
    if (spokenQuestionKeyRef.current === key) return
    spokenQuestionKeyRef.current = key
    speakQuestionText(question.prompt, bridge)
  }, [
    bridge,
    cancelSpeech,
    question,
    speakMuted,
    speakQuestionText,
    stopListening,
    synthesisOk,
  ])

  // Tear down speech engines on unmount.
  useEffect(() => {
    return () => {
      stopListening()
      cancelSpeech()
    }
  }, [cancelSpeech, stopListening])

  const handleSubmit = useCallback(async () => {
    if (!session || !question || submitting) return
    // Stop mouth + ear before POST so we never keep listening across steps.
    stopListening()
    cancelSpeech()
    setSubmitError(null)

    let answer: Record<string, unknown>
    if (question.id === 'three_am') {
      // Whitespace only — never rewrite the digits or invent a country code.
      const cleanedPhone = phone.replace(/\s+/g, '')
      if (consent && !cleanedPhone) {
        // Consent with no number is meaningless — never POST that pairing.
        setSubmitError(
          'Add the number we should call, or leave the 3am call off. We won’t record consent without a way to reach you.',
        )
        return
      }
      if (cleanedPhone && !/^\+[1-9]\d{6,14}$/.test(cleanedPhone)) {
        // The backend only accepts international (E.164) numbers, and we never
        // guess a country code on the client's behalf — ask, don't rewrite.
        setSubmitError(
          'That number needs to be in international format, starting with + and the country code (for example +64 21 555 1234). Your number is still here exactly as you typed it.',
        )
        return
      }
      answer = {
        escalation_contact_phone_e164: cleanedPhone || null,
        three_am_call_consent: consent,
      }
    } else {
      answer = composeAnswer(question.id, draft)
    }

    setSubmitting(true)
    try {
      const res = await answerInterview(session.id, {
        step: question.id,
        answer,
        bridge: true,
      })
      if (res.next_question === null) {
        // Last answer in — close the interview and show the warm close.
        const done = await completeInterview(session.id)
        setBridge(res.bridge)
        setSession(done)
        setDraft('')
        setPhone('')
        setConsent(false)
      } else {
        setBridge(res.bridge)
        setSession(res)
        // Only clear the client's words once they are safely stored.
        setDraft('')
        setPhone('')
        setConsent(false)
      }
    } catch {
      // Preserve exactly what the client typed and offer a retry — never
      // silently drop their words.
      setSubmitError(
        'We couldn’t save that just now. Your answer is still here. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }, [
    cancelSpeech,
    consent,
    draft,
    phone,
    question,
    session,
    stopListening,
    submitting,
  ])

  // See `embedded` above: heading level follows the host, never the copy.
  const QuestionHeading = embedded ? ('h2' as const) : ('h1' as const)

  const body = (
    <>
        {loading ? (
          <div
            className="flex items-center justify-center py-16 text-gray-400"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
            <span className="ml-2 text-sm">Getting ready…</span>
          </div>
        ) : loadError ? (
          <div
            className="flex flex-col gap-3 rounded-2xl border border-danger/30 bg-danger-light p-4 text-sm text-danger"
            role="alert"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
              <span>{loadError} Nothing was started.</span>
            </div>
            <button
              type="button"
              onClick={() => void load()}
              className="tap-min self-start rounded-lg bg-danger px-3.5 py-2 text-sm font-medium text-white hover:bg-danger/90 transition-colors"
            >
              Try again
            </button>
          </div>
        ) : session && session.status === 'complete' ? (
          <CompleteView embedded={embedded} onDone={onDone} />
        ) : question ? (
          <div>
            {/* Who's speaking — the Concierge, sitting down with you. */}
            <div className="mb-6 flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brass/10">
                <ShieldCheck className="h-5 w-5 text-brass" aria-hidden="true" />
              </span>
              <div className="leading-tight">
                <p className="text-sm font-semibold text-navy">Your Concierge</p>
                <p className="text-[11px] text-gray-500" aria-live="polite">
                  {question.number} of {total || '…'}
                </p>
              </div>
            </div>

            {/* Slide the question in fresh each step (keyed by id). */}
            <motion.div
              key={question.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {bridge && (
                <p className="mb-3 flex items-start gap-1.5 text-sm italic text-gray-500">
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brass" aria-hidden="true" />
                  <span>{bridge}</span>
                </p>
              )}

              {/* First-meeting intro — copy only; never paraphrases the server question. */}
              {session?.current_step === 1 &&
                Object.keys(session.answers ?? {}).length === 0 && (
                  <p className="mb-3 text-sm text-gray-600">
                    This is our first meeting. One question at a time.
                  </p>
                )}

              {/* The question — rendered EXACTLY as the backend sends it.
                  Embedded on home it is an h2: home's own h1 ("Your
                  dashboard") already owns the page, and the question is a
                  section of it, not a second document title. */}
              <QuestionHeading className="text-xl font-semibold leading-snug text-navy">
                {question.prompt}
              </QuestionHeading>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  void handleSubmit()
                }}
                className="mt-5"
              >
                {question.id === 'three_am' ? (
                  // Dedicated fields — this step's answer feeds the real
                  // out-of-band call path, so no prose sink here.
                  // No Talk/mic: never dictate the phone or flip 3am consent.
                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="three-am-phone"
                        className="block text-xs font-medium text-navy"
                      >
                        Best number
                      </label>
                      <input
                        id="three-am-phone"
                        type="tel"
                        inputMode="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+64 21 555 123"
                        className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brass/40"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        The number your analyst would actually call. Leave it
                        blank if you’d rather decide later.
                      </p>
                    </div>
                    <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-gray-200 bg-white p-3">
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-0.5 accent-brass"
                      />
                      <span className="text-sm leading-relaxed text-navy">
                        A human analyst may call this number at 3am if it’s
                        genuinely serious.
                      </span>
                    </label>
                  </div>
                ) : (
                  <>
                    <label htmlFor="interview-answer" className="sr-only">
                      Your answer
                    </label>
                    <textarea
                      id="interview-answer"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      rows={7}
                      placeholder="Take your time, in your own words."
                      aria-label="Your answer"
                      className="w-full resize-y rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm leading-relaxed text-navy placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-brass/40"
                    />
                    {/* Free browser mic — same draft the Continue button posts. */}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {recognitionOk ? (
                        listening ? (
                          <button
                            type="button"
                            onClick={() => stopListening()}
                            className="tap-min inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-navy hover:bg-gray-50 transition-colors"
                          >
                            <MicOff className="h-3.5 w-3.5" aria-hidden="true" />
                            Stop
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startListening()}
                            className="tap-min inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-navy hover:bg-gray-50 transition-colors"
                          >
                            <Mic className="h-3.5 w-3.5" aria-hidden="true" />
                            Talk
                          </button>
                        )
                      ) : (
                        <p className="text-xs text-gray-500" role="status">
                          Voice typing isn’t available in this browser. You can
                          still type your answer.
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Speak the server prompt (+ bridge) — never paraphrased. */}
                {synthesisOk && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSpeakMuted(false)
                        speakQuestionText(question.prompt, bridge)
                      }}
                      className="tap-min inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-navy hover:bg-gray-50 transition-colors"
                    >
                      <Volume2 className="h-3.5 w-3.5" aria-hidden="true" />
                      Speak
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSpeakMuted(true)
                        cancelSpeech()
                      }}
                      className="tap-min inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-navy hover:bg-gray-50 transition-colors"
                      aria-pressed={speakMuted}
                    >
                      <VolumeX className="h-3.5 w-3.5" aria-hidden="true" />
                      Mute
                    </button>
                  </div>
                )}

                {voiceNote && (
                  <p className="mt-2 text-xs text-gray-500" role="status">
                    {voiceNote}
                  </p>
                )}

                {submitError && (
                  <div
                    className="mt-3 flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-light p-3 text-sm text-danger"
                    role="alert"
                  >
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{submitError}</span>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <p className="text-xs text-gray-400">
                    One question at a time. You can come back to this any time.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="tap-min inline-flex items-center gap-1.5 rounded-lg bg-brass px-5 py-2.5 text-sm font-medium text-white hover:bg-brass-dark disabled:opacity-50 transition-colors"
                  >
                    {submitting && (
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    )}
                    {question.number >= total && total > 0 ? 'Finish' : 'Continue'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        ) : (
          // In-progress but no question left: the finalise effect is closing it.
          <div
            className="flex items-center justify-center py-16 text-gray-400"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
            <span className="ml-2 text-sm">Wrapping up…</span>
          </div>
        )}
    </>
  )

  // Embedded: no second nav, no second <main>, no min-h-screen — home already
  // painted all three, and the meeting is one section of that page.
  if (embedded) {
    return <div className="max-w-[560px]">{body}</div>
  }

  return (
    <SocShell mainClassName="box-border w-full max-w-[560px] mx-auto px-4 py-8">{body}</SocShell>
  )
}

// The warm close. A finished interview is words saved — not a console, not a
// chat desk, not watching. One honest door out: a completed meeting means the
// next signed-in stop is home (/client for everyone, including org admin), including
// this same session — but the client chooses when; the close never
// auto-redirects, and it never starts a second meeting.
//
// Embedded on /client home there is nowhere to go — they are already home, and
// navigating there would be a dead button — so the door hands back to the host
// (`onDone`), which drops the meeting section and leaves home standing, with
// the tripwire band and its Word decoy exactly where they were.
function CompleteView({
  embedded = false,
  onDone,
}: {
  embedded?: boolean
  onDone?: () => void
} = {}) {
  const navigate = useNavigate()
  const CloseHeading = embedded ? ('h2' as const) : ('h1' as const)
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm"
    >
      <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success-light">
        <ShieldCheck className="h-6 w-6 text-success" aria-hidden="true" />
      </span>
      <CloseHeading className="text-xl font-semibold text-navy">
        Thank you. That was our first meeting.
      </CloseHeading>
      <p className="mt-2 text-sm text-gray-600">
        We have your words, and they’re saved. This meeting is done.
      </p>
      <button
        type="button"
        onClick={() => {
          if (onDone) {
            onDone()
            return
          }
          navigate('/client')
        }}
        className="tap-min mt-6 inline-flex items-center rounded-lg bg-brass px-5 py-2.5 text-sm font-medium text-white hover:bg-brass-dark transition-colors"
      >
        Continue
      </button>
    </motion.div>
  )
}
